import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from 'pinia';
import type { WebLocale } from '@/enums/web-locale';
import type { Package } from '@/@types/graphql-types';
import type { FilterCollection, FilterName } from '@/stores/useFilterStoreTypes';

import { PackagesSelectionModal } from '@/lazy-load-components';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';

import { calculateUrl } from '@/helpers/state-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { useQuery } from '@/helpers/composables/useApollo';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';
import { NewPageType } from '@/@types/graphql-types';

import {
	GetUpcomingProvidersDocument,
	GetUpcomingProvidersQuery,
	GetUpcomingProvidersQueryVariables,
} from '@/pages/graphql/queries/GetUpcomingProviders.query';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

import { useExperiment } from '@/libraries/experiment';

export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBarProvider',
  props: {
    collectionType: { default: CollectionType.NEW },
    newPageType: { default: NewPageType.New },
    currentFilters: null,
    providers: { default: () => [] },
    showAddButton: { type: Boolean, default: false },
    iconSize: { default: 'regular' },
    providersClickable: { type: Boolean, default: true },
    webLocale: null,
    updateRouteState: { type: Boolean, default: true },
    trackEvent: { type: Boolean, default: true },
    userSelectedPackages: null,
    buyboxType: null
  },
  emits: ["toggle"],
  setup(__props: any, { emit }: { emit: ({ (e: 'toggle', payload: any): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { collectionType: CollectionType, newPageType: NewPageType, currentFilters: FilterCollection, providers: Package[], showAddButton: boolean, iconSize: 'regular' | 'small', providersClickable: boolean, webLocale: WebLocale, updateRouteState: boolean, trackEvent: boolean, userSelectedPackages?: string[], buyboxType?: 'grid' | 'list' };





const plusIcon = computed(() => `/${ASSETS_DIR}/img/plus_icon.png`);
const IMG_PROVIDERS_BAR_LOAD_COUNT = globalThis.IMG_PROVIDERS_BAR_LOAD_COUNT;

const route = useRoute();
const router = useRouter();
const { language, country } = storeToRefs(useLanguageStore());

const queryVariables = computed(() => ({
	country: country.value,
	language: language.value,
	pageType: NewPageType.Upcoming,
	first: 32,
}));

const upcomingProviderRoutes = ['app.titles.upcoming', 'app.titles.new', 'app.titles.pricedrops'];

const { result, onError } = useQuery<GetUpcomingProvidersQuery, GetUpcomingProvidersQueryVariables>(
	GetUpcomingProvidersDocument,
	queryVariables,
	() => ({
		enabled: upcomingProviderRoutes.some(routeName => route?.name?.startsWith(routeName)),
		errorPolicy: 'all',
	})
);

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Upcoming Providers Data]',
		{ error, where: 'Component: FilterBarProvider.vue' },
		'error'
	);
});

const upcomingProviderIds = computed(() => {
	const packageIds = new Set();
	(result.value?.newTitles.edges ?? [])
		.flatMap(edge => edge.node.content.upcomingReleases)
		.forEach(release => (release.package?.packageId ? packageIds.add(release.package?.packageId) : void 0));

	return packageIds;
});

const filterStore = useFilterStore();

const filteredProviders = computed(() => props.providers.filter(p => p.isVisibleFilterBar));

const showPlusIcon = computed(() => filterStore.selectedProvidersType === 'my_services' || props.showAddButton);

const isPriceDropsPage = computed(
	() => props.collectionType === CollectionType.NEW && route.meta?.newPageType === NewPageType.PriceDrop
);

// provider links shouldn't exist if it is a pricedrops url, because they only exist as query params, e.g. /us/on-sale?providers=itu
const providerTag = computed(() => (isPriceDropsPage.value ? 'button' : 'a'));

const { subgenresList, allProvidersById, providersByShortName, genresByShortName } = storeToRefs(useConstantStore());

// SSR perf: only call providersById once instead of every v-for loop.
const providersById = ref<Record<number, Package>>(allProvidersById.value);
watch(
	() => props.providers.length,
	() => (providersById.value = allProvidersById.value)
);

const providerUrls = ref(getProviderUrls());

watch([() => props.webLocale, isPriceDropsPage], () => {
	providerUrls.value = getProviderUrls();
});

function getProviderUrls() {
	const providerUrls: Record<string, string> = {};

	// If not on the pricedrops page generate provider URLs
	if (!isPriceDropsPage.value) {
		// for perf reasons, avoid accessing .value on every loop of `forEeach`
		// since we only want simple provider page urls with no query strings, can pass genres = {}, subgenres = []
		const cachedProvidersByShortName = providersByShortName.value;
		const cachedGenresByShortName = genresByShortName.value;
		const cachedSubgenresList = subgenresList.value;

		props.providers.forEach(provider => {
			providerUrls[provider.shortName] = calculateUrl(
				router,
				undefined,
				props.collectionType,
				{
					// we only want content_types and providers in the <a href>.
					// no ?genres=crm or other filters.
					content_types: props.currentFilters.content_types,
					providers: [provider.shortName],
				},
				cachedProvidersByShortName,
				// To do: These last two can be replaced with {}, [] - if we dont need the query strings etc.. and just want to build simple links and improve performance (MW)
				cachedGenresByShortName,
				cachedSubgenresList
			);
		});
	}

	return providerUrls;
}

// EXP_PROVIDER_BUYBOX
const { trackEvent } = useExperiment('EXP_PROVIDER_BUYBOX');
// EXP_PROVIDER_BUYBOX

async function providerClicked(provider: Package) {
	if (!props.providersClickable) return;

	const toggleData = {
		filterName: 'providers' as FilterName,
		value: provider.shortName,
		selectedState: (isFiltersContainsProvider(provider.shortName) ? 0 : 1) as 0 | 1,
		updateRouteState: props.updateRouteState,
		userSelectedPackages: props.userSelectedPackages,
		trackEvent: props.trackEvent,
	};

	// EXP_PROVIDER_BUYBOX
	let _buyboxType = 0;
	if (props.buyboxType === 'grid') _buyboxType = 0;
	if (props.buyboxType === 'list') _buyboxType = 1;

	trackEvent({
		action: 'click',
		label: 'provider_toggle',
		property: provider.technicalName,
		value: _buyboxType,
	});
	// EXP_PROVIDER_BUYBOX

	const newRoute = await filterStore.toggle(toggleData);
	emit('toggle', toggleData);

	if (newRoute) router.push(newRoute);
}

function isFiltersContainsProvider(providerName: string) {
	return props.currentFilters.providers.includes(providerName);
}

function isProviderActive(providerName: string) {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
}

function isProviderDisabled(providerId: number) {
	return props.collectionType === CollectionType.UPCOMING && !upcomingProviderIds.value.has(providerId);
}

async function openProviderSelection() {
	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
}

return { __sfc: true,props, emit, plusIcon, IMG_PROVIDERS_BAR_LOAD_COUNT, route, router, language, country, queryVariables, upcomingProviderRoutes, result, onError, upcomingProviderIds, filterStore, filteredProviders, showPlusIcon, isPriceDropsPage, providerTag, subgenresList, allProvidersById, providersByShortName, genresByShortName, providersById, providerUrls, getProviderUrls, trackEvent, providerClicked, isFiltersContainsProvider, isProviderActive, isProviderDisabled, openProviderSelection, HiddenHorizontalScrollbar, ProviderIcon }
}

})